import { HttpClient } from '@angular/common/http';
import { Inject, inject, Injectable, Injector } from '@angular/core';
import { BullhornAuthService, InveniasAuthService, NoAuthService, SalesforceAuthService } from 'auth-service-lib';
import {
  BullhornDataSourceApiService,
  BullhornFileTypes,
  BullhornListTypes,
  BullhornNamespace,
  SalesforceDataSourceApiService,
  SalesforceFileTypes,
  SalesforceListTypes,
  SalesforceNamespace,
} from 'datasource-service-lib';
import { InveniasDataSourceApiService, InveniasFileTypes, InveniasListTypes, InveniasNamespace } from 'datasource-service-lib';
import { InveniasMockDatasourceApiService, InveniasMockNamespace } from 'datasource-service-lib';
import { LocalFileNamespace } from 'datasource-service-lib';
import { BullhornComposeMailService, IComposeMailService } from 'email-service-lib';
import { ENVIRONMENT, RUNTIME_ENVIRONMENT } from 'environments-lib';
import { BullhornIconService, SalesforceIconService } from 'icon-service-lib';
import { InveniasIconService } from 'icon-service-lib';
import { IAuthService, IDataSourceApiService, IFileDataSourceApiService, IEnvironment } from 'interfaces-lib';
import { IIconService } from 'interfaces-lib';
import { IWorkflowService } from 'interfaces-lib';
import { IdentityStorage } from 'storage-service-lib';
import { StandardWorkflowService } from 'workflow-task-service-lib';
import { environment } from '../../environments/environment';
import { IEntityProfileService } from '../app-entity-preview/ientityprofileservice';
import { IRecentActivityProfileService } from '../common-components/recent-activity/irecentactivityprofileservice';
import { IFileListProvider } from '../interfaces/ifilelistprovider';
import { IFileToPathConverter } from '../interfaces/ifiletopathconverter';
import { BullhornEntityProfileService } from './bullhorn/bullhorn.entityprofile.service';
import { BullhornFileToPathConverter } from './bullhorn/bullhorn.filetopathconverter';
import { BullhornRecentActivityProfileService } from './bullhorn/bullhorn.recentactivityprofile.service';
import { DocumentClientDatasourceApiListServiceFactory } from './datasource/document-client-datasource-list.service';
import { InveniasEntityProfileService } from './invenias/invenias.entityprofile.service';
import { InveniasFileToPathConverter } from './invenias/invenias.filetopathconverter';
import { InveniasRecentActivityProfileService } from './invenias/invenias.recentactivityprofile.service';
import { LocalFileFileToPathConverter } from './localFile.filetopathconverter';
import { SalesforceEntityProfileService } from './salesforce/salesforce.entityprofile.service';
import { SalesforceFileToPathConverter } from './salesforce/salesforce.filetopathconverter';
import { SalesforceRecentActivityProfileService } from './salesforce/salesforce.recentactivityprofile.service';

type NamespaceName = string;
type ServiceName = string;

@Injectable({ providedIn: 'root' })
export class ServiceProviderService {
  private _servicesCache = new Map<NamespaceName, Map<ServiceName, any>>();
  private _wfsCache = new Map<string, IWorkflowService>();
//@TODO: fix environment part
  constructor(
    private http: HttpClient,
    private injector: Injector,
    private identityStorage: IdentityStorage,
    @Inject(ENVIRONMENT) private environment: Partial<IEnvironment>
  ) {
    // force creation of service so the singleton instance is created
    // TODO: remove when we fix the datsource api service
    inject(DocumentClientDatasourceApiListServiceFactory);
  }

  getDataSourceApiService(namespace: string): IDataSourceApiService {
    const services = ServiceProviderService.putIfNotPresent(
      namespace,
      this._servicesCache,
      () => new Map<ServiceName, IDataSourceApiService>()
    );

    let maker: () => IDataSourceApiService = () => undefined;

    if (namespace === InveniasNamespace.name) {
      maker = () =>
        new InveniasDataSourceApiService(
          this.http,
          this.identityStorage,
          this.injector.get(ENVIRONMENT),
          this.injector.get(RUNTIME_ENVIRONMENT)
        );
    }

    if (namespace === InveniasMockNamespace.name) {
      maker = () => new InveniasMockDatasourceApiService();
    }

    if (namespace === BullhornNamespace.name) {
      maker = () => new BullhornDataSourceApiService(this.http, this.identityStorage);
    }

    if (namespace === SalesforceNamespace.name) {
      maker = () => new SalesforceDataSourceApiService(this.http, this.identityStorage, this.environment);
    }

    return ServiceProviderService.putIfNotPresent('datasourceApiService', services, maker);
  }

  // not sure how this will be used yet
  getWorkflowService(namespace: string): IWorkflowService {
    return ServiceProviderService.putIfNotPresent(
      namespace,
      this._wfsCache,
      () => new StandardWorkflowService(this.http, `${environment.kpWorkflowHost}`, environment.kpWorkflowStatus)
    );
  }

  // Todo: remove?
  getAuthService(namespace: string): IAuthService {
    if (namespace === InveniasNamespace.name) {
      return this.injector.get(InveniasAuthService);
    }

    if (namespace === InveniasMockNamespace.name) {
      return new NoAuthService();
    }

    if (namespace === BullhornNamespace.name) {
      return this.injector.get(BullhornAuthService);
    }
    if (namespace === SalesforceNamespace.name) {
      return this.injector.get(SalesforceAuthService);
    }

    return undefined;
  }

  getEntityProfileService(namespace: string): IEntityProfileService {
    if (namespace === InveniasNamespace.name || namespace === InveniasMockNamespace.name) {
      return this.injector.get(InveniasEntityProfileService);
    }

    if (namespace === BullhornNamespace.name) {
      return this.injector.get(BullhornEntityProfileService);
    }

    if (namespace === SalesforceNamespace.name) {
      return this.injector.get(SalesforceEntityProfileService);
    }

    return undefined;
  }

  // Todo: remove?
  getIconService(namespace: string): IIconService {
    if (namespace === InveniasNamespace.name || namespace === InveniasMockNamespace.name) {
      return this.injector.get(InveniasIconService);
    }
    if (namespace === BullhornNamespace.name) {
      return this.injector.get(BullhornIconService);
    }

    if (namespace === SalesforceNamespace.name) {
      return this.injector.get(SalesforceIconService);
    }

    return undefined;
  }

  getRecentActivityProfileService(namespace: string): IRecentActivityProfileService {
    if (namespace === InveniasNamespace.name || namespace === InveniasMockNamespace.name) {
      return this.injector.get(InveniasRecentActivityProfileService);
    }

    if (namespace === BullhornNamespace.name) {
      return this.injector.get(BullhornRecentActivityProfileService);
    }

    if (namespace === SalesforceNamespace.name) {
      return this.injector.get(SalesforceRecentActivityProfileService);
    }

    return undefined;
  }

  getFileToPathConverter(namespace: string): IFileToPathConverter {
    if (namespace === InveniasNamespace.name || namespace === InveniasMockNamespace.name) {
      return new InveniasFileToPathConverter(InveniasListTypes.Documents);
    }

    if (namespace === BullhornNamespace.name) {
      return new BullhornFileToPathConverter(BullhornListTypes.FileAttachments);
    }

    if (namespace === SalesforceNamespace.name) {
      return new SalesforceFileToPathConverter(SalesforceListTypes.FileAttachments);
    }
    if (namespace === LocalFileNamespace.name) {
      return new LocalFileFileToPathConverter();
    }

    return undefined;
  }

  // TODO: create an injectable version + get the xxxx.filelistviewconfig to use instead of crufting its own
  getFileListProvider(namespace: string): IFileListProvider {
    if (namespace === InveniasNamespace.name) {
      const dataSourceApiService = this.getDataSourceApiService(namespace);
      return {
        dataSourceApiService: dataSourceApiService,
        listType: InveniasListTypes.Documents,
        fileType: InveniasFileTypes.Document,
      };
    }

    if (namespace === BullhornNamespace.name) {
      const dataSourceApiService = this.getDataSourceApiService(namespace);
      return {
        dataSourceApiService: dataSourceApiService,
        listType: BullhornListTypes.FileAttachments,
        fileType: BullhornFileTypes.FileAttachment,
      };
    }

    if (namespace === SalesforceNamespace.name) {
      const dataSourceApiService = this.getDataSourceApiService(namespace);
      return {
        dataSourceApiService: dataSourceApiService,
        listType: SalesforceListTypes.FileAttachments,
        fileType: SalesforceFileTypes.FileAttachment,
      };
    }

    throw new Error(`Unknown namespace '${namespace}'.`);
  }

  getComposeMailService(namespace: string): IComposeMailService {
    if (namespace === BullhornNamespace.name) {
      return this.injector.get(BullhornComposeMailService);
    }
    return undefined;
  }

  private static putIfNotPresent(key: string, map: Map<string, any>, maker: () => any): any {
    let value = map.get(key);
    if (value === undefined) {
      value = maker();
      map.set(key, value);
    }
    return value;
  }
}
